import React, { useMemo } from 'react'
import { Box } from '@mui/material'
import { Building2 } from 'lucide-react'
import { Avatar } from '@repo/shadcn-ui'

/**
 * The distinction this component exists to name: a PERSON is a portrait and gets a
 * circle; a COMPANY is a logo and gets a square.
 *
 * A brand mark is a designed lockup — a monogram, a wordmark, a stacked signature —
 * drawn to a rectangular bounding box. Circle-cropping one cuts the corners off a
 * monogram and slices the ends off a wordmark, which is how the Louis Vuitton mark on
 * the provenance "Certificate created" row lost its serifs. A face survives a circle
 * crop because the subject is centred and roughly round; a logo does not.
 *
 * Several surfaces render BOTH kinds through one code path (the provenance rows, the
 * transfer summary, the pending-transfer card), so the shape MUST be chosen from what
 * the entity IS — never from which screen it happens to be on. Hence an explicit
 * `entity` prop rather than a per-call-site `shape`: a caller that knows it is drawing
 * a company cannot accidentally ask for a circle.
 *
 * The person branch delegates straight to the shared `@repo/shadcn-ui` Avatar so the
 * circular rendering is byte-identical to what it has always been — adopting this
 * component can only change companies.
 *
 * NOTE (layering, working-conventions §47): the durable home for this is a `shape`
 * prop on `packages/shadcnUi/components/display/avatar`, which already carries the
 * `noImageIcon: 'company' | 'profile'` distinction and therefore already knows which
 * entity it is drawing. That package was owned by another agent when this landed, so
 * the square lives here at the app layer for now. Fold it down when the package is
 * free, and delete this file's company branch rather than letting the two drift.
 */
export type AvatarEntity = 'person' | 'company'

/** Matches the small image tiles elsewhere in the app (the certificate list product
 *  thumbnail, the certificate picker's 36px tile, the provenance card itself) — all
 *  8px. A hard 0 would read as a raw image dropped onto the row rather than a tile. */
export const COMPANY_TILE_RADIUS = '8px'

export interface EntityAvatarProps {
  /** What the thing IS. Drives the shape; never infer this from the screen. */
  entity: AvatarEntity
  src?: string | null
  /** Used for the alt text and to derive the initials fallback. */
  name?: string | null
  /** Explicit initials. Falls back to deriving them from `name`. */
  initials?: string
  /** Rendered box, in px. Both shapes occupy exactly this footprint. */
  size?: number
}

const deriveInitials = (name?: string | null): string | undefined => {
  if (!name) return undefined
  /* Strip a leading '@' first, so a HANDLE initials as its letters rather than its
     punctuation. VERA-304: `getOwner` now labels a name-less person `@spencer2` (the
     shared `resolveUserIdentity` contract), and without this the provenance card's
     avatar read "@S" — `UserPreview` already strips it for exactly this reason, and two
     avatars for one person disagreeing about their initials is the drift §36 warns of. */
  const parts = name.trim().replace(/^@/, '').split(/\s+/).filter(Boolean)
  if (parts.length === 0) return undefined
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

/** The shadcn Avatar takes size TOKENS, not px. Map so a caller can think in pixels. */
const nearestShadcnSize = (size: number): 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' => {
  if (size <= 24) return 'xs'
  if (size <= 32) return 'sm'
  if (size <= 40) return 'md'
  if (size <= 48) return 'lg'
  if (size <= 64) return 'xl'
  return '2xl'
}

export const EntityAvatar: React.FC<EntityAvatarProps> = ({
  entity,
  src,
  name,
  initials: initialsProp,
  size = 40,
}) => {
  const initials = useMemo(
    () => initialsProp ?? deriveInitials(name),
    [initialsProp, name],
  )

  if (entity === 'person') {
    return (
      <Avatar
        src={src || undefined}
        initials={initials}
        noImageIcon="profile"
        size={nearestShadcnSize(size)}
      />
    )
  }

  const hasImage = !!src
  /* `contain` is only half the fix. A logo letterboxed hard against the tile edge
     still reads as cropped, and a WIDE wordmark would run edge to edge while a TALL
     lockup floats with air on the sides — so a small inset gives every aspect ratio
     the same margin and the tile reads as a frame rather than a bleed. Proportional
     so a 24px chip and a 64px header tile get the same optical breathing room.

     HALVED from 0.08 (VERA-304). With the border gone (see below) the chrome per
     side went 3px inset + 1px border = 4px down to 2px, so at the common 40px size
     the mark occupies 36 of 40px instead of 32 — 80% → 90% linear, 64% → 81% by
     area. The inset is not taken to 0: that is what the paragraph above argues
     against, and `contain` still needs the margin to keep a wide wordmark off the
     8px corner radius. */
  const inset = Math.max(1, Math.round(size * 0.05))

  return (
    <Box
      sx={theme => ({
        width: size,
        height: size,
        flexShrink: 0,
        /* Square with a small radius — see COMPANY_TILE_RADIUS. */
        borderRadius: COMPANY_TILE_RADIUS,
        /*
         * NO BORDER — the tile bounds ITSELF in both regimes, so a border can only
         * add chrome (VERA-304; reported as "logos have weird border they
         * shouldn't … should fill the square space").
         *
         * The instinct is that dropping it makes a white tile vanish on a white
         * card, and the raw number says so — measured live, the tile's `#FFFFFF`
         * against the `background.paper` card is **1.000:1**, and against the
         * 48px `#F4F5F5` icon well it is **1.092:1**. But "the plate is invisible"
         * is the DESIRED outcome here, not the defect:
         *
         * | backdrop | plate reads | border adds |
         * |---|---|---|
         * | white card `#FFFFFF` (measured) | **1.00:1** — invisible, so you see the MARK edge to edge | a box around a logo |
         * | icon well `#F4F5F5` (measured) | 1.09:1 hairline | a second, competing edge |
         * | dark paper `#0f0f12` (`@repo/ui` dark palette) | **19.14:1** — unmissable | nothing |
         * | grey fallback `#CED0D3` on white | 1.55:1 — self-bounding | nothing |
         *
         * The number that actually decided it: the border rendered `#E4E4E7`, which
         * is **1.27:1** against the white plate and **1.22:1** against the grey
         * fallback fill. In every case where a boundary was genuinely needed, the
         * FILL already provided a stronger edge (1.55:1 light, 12–19:1 dark) than
         * the border did. It was always the weakest line on the tile — it read as
         * chrome because that is all it ever was.
         *
         * A border would only carry information on a MID-TONE backdrop near white,
         * and no such surface exists: `background.paper` is white in the light
         * theme and `#0f0f12` in the dark one, and Studio theming only sets
         * `background.default` (the page), never the card. So this is the
         * "treatment that works on both" rather than a theme-aware branch — it
         * needs no knowledge of what it sits on, which matters because a shared
         * component cannot see its own backdrop.
         *
         * Two things measured while making this call, both load-bearing:
         *  1. The authored colour was already a lie. `#vera-shadow-root *` sets
         *     `border-color` with ID specificity, so `grey[300]` rendered
         *     `#E4E4E7` — the same trap `stateCardSurface.ts` documents. Nobody
         *     was seeing the colour this file asked for.
         *  2. The white "plate" in the bug screenshot is NOT part of the logo.
         *     The asset is a 512×512 PNG that is **75.1% transparent with all four
         *     corners at alpha 0** — a dark mark on transparency. So the plate is
         *     this tile's own background showing through, and it is kept (below)
         *     precisely because a transparent dark mark needs a guaranteed light
         *     field if this ever renders on a dark surface. Keeping the FIELD and
         *     dropping the FRAME are independent decisions; only the frame was
         *     doing no work.
         */
        /* White behind a real logo (brand marks are drawn for white); the neutral
           grey of the shared avatar behind a fallback, so an empty tile is not a
           white void on a white card. */
        backgroundColor: hasImage ? theme.palette.common.white : theme.palette.grey[200],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        padding: `${inset}px`,
        boxSizing: 'border-box',
      })}
    >
      {hasImage ? (
        <Box
          component="img"
          src={src as string}
          alt={name || ''}
          sx={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            /* Never squeeze the mark to fill the square — a stretched logo is as
               wrong as a cropped one. */
            objectFit: 'contain',
            display: 'block',
          }}
        />
      ) : initials ? (
        /* The fallback occupies the SAME square as the logo it stands in for. A
           circular fallback beside square logos would make one list of companies
           render as a mix of shapes, which is the bug this rule exists to prevent. */
        <Box
          component="span"
          sx={theme => ({
            fontSize: Math.round(size * 0.4),
            lineHeight: 1,
            fontWeight: 400,
            color: theme.palette.grey[500],
          })}
        >
          {initials.slice(0, 2).toUpperCase()}
        </Box>
      ) : (
        <Building2
          size={Math.round(size * 0.5)}
          /* Matches the `text-gray-500` the shared avatar gives every no-image glyph. */
          color="#6b7280"
        />
      )}
    </Box>
  )
}

export default EntityAvatar
